import {
  Box,
  BoxProps,
  Button,
  Icon,
  Image,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiFileUploadLine } from "@remixicon/react";
import { useEffect, useRef, useState } from "react";
import { useBgHover } from "../../themes/colors";
import useCustomToast from "../../lib/useCustomToast";

interface Props extends BoxProps {
  onFileChange: (file: any | undefined) => void;
  onHandleDrop: (file: any | undefined) => void;
  initValue?: string;
  inputName?: string;
  isError?: boolean;
  placeholder?: string;
}

export const ImageInput = ({
  onFileChange,
  onHandleDrop,
  initValue,
  inputName = "img",
  isError = false,
  placeholder = "Geser foto anda disini atau klik untuk memilih",
  ...rest
}: Props) => {
  const [previewImage, setPreviewImage] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const bgHover = useBgHover();
  const toast = useCustomToast();

  useEffect(() => {
    initValue ? setPreviewImage(initValue) : setPreviewImage("");
  }, [initValue]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null; // Get the selected file
    if (file) {
      // Check if the file type is allowed
      const allowedTypes = ["image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Upload file dengan format JPEG atau PNG",
        });
        return; // Exit the function if the type is not allowed
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string); // Set the image URL to Avatar's src

        //original code
        // onFileChange(file); // Pass the file to the parent component
      };

      //modified code
      onFileChange(file); // Pass the file to the parent component
      reader.readAsDataURL(file);
    } else {
      onFileChange(null); // If no file is selected, pass null
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onHandleDrop(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Box
        px={4}
        py={previewImage ? 4 : 10}
        border={isError ? "3px solid" : "1px dashed"}
        borderColor={isError ? "red.400" : "gray.400"}
        borderRadius={"md"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        flexDirection={"column"}
        cursor={"pointer"}
        _hover={{ bg: bgHover }}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        {...rest}
      >
        {previewImage ? (
          <Image
            src={previewImage}
            w={"100%"}
            maxW={"200px"}
            alt="Image Preview"
            objectFit="contain"
            objectPosition={"center"}
            style={{ aspectRatio: 4 / 3 }}
          />
        ) : (
          <>
            <VStack fontSize={"xs"}>
              <Icon
                as={RiFileUploadLine}
                color={"teal.400"}
                fontSize={"72px"}
              />

              <Text textAlign={"center"}>{placeholder}</Text>
              <Text color="gray.500">(JPG, JPEG, PNG)</Text>
            </VStack>
          </>
        )}
      </Box>

      <Input
        ref={inputRef}
        id={inputName}
        type="file"
        name={inputName}
        accept="image/jpeg, image/png"
        display={"none"}
        onChange={(event) => {
          handleFileChange(event);
        }}
      />

      {previewImage && (
        <Button
          variant="ghost"
          colorScheme="red"
          onClick={() => {
            setPreviewImage("");
            onFileChange(undefined);
            onHandleDrop(undefined);
          }}
        >
          Hapus Foto
        </Button>
      )}
    </>
  );
};
